import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Plus, Search, Loader2, ThumbsUp, ThumbsDown, CheckCircle, Users,
} from 'lucide-react';
import { format, parseISO, isPast, differenceInHours, differenceInDays } from 'date-fns';
import { governanceAPI } from '../../api';
import { toast } from 'sonner';

const DeadlineBadge = ({ deadline, compact }) => {
  if (!deadline) return null;
  const d = parseISO(deadline);
  let status = 'ok';
  if (isPast(d)) status = 'overdue';
  else if (differenceInHours(d, new Date()) <= 24) status = 'urgent';
  else if (differenceInDays(d, new Date()) <= 3) status = 'near';

  const colors = { overdue: 'bg-red-100 text-red-800 border-red-300', urgent: 'bg-orange-100 text-orange-800 border-orange-300', near: 'bg-amber-100 text-amber-800 border-amber-300', ok: 'bg-green-100 text-green-800 border-green-300' };
  return <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${colors[status]}`}>{compact ? format(d, 'MMM d') : format(d, 'MMM d, yyyy')}</span>;
};

const PollsTab = ({ polls, user, searchTerm, setSearchTerm, fetchData }) => {
  const [pollDialog, setPollDialog] = useState(false);
  const [pollForm, setPollForm] = useState({ title: '', description: '', options: ['', ''], voter_group: 'all', deadline: '', anonymous: false });
  const [creatingPoll, setCreatingPoll] = useState(false);

  const handleCreatePoll = async () => {
    if (!pollForm.title || pollForm.options.filter(o => o.trim()).length < 2) {
      toast.error('Title and at least 2 options required'); return;
    }
    setCreatingPoll(true);
    try {
      await governanceAPI.createPoll({ ...pollForm, options: pollForm.options.filter(o => o.trim()), deadline: pollForm.deadline || null });
      toast.success('Poll created');
      setPollDialog(false);
      setPollForm({ title: '', description: '', options: ['', ''], voter_group: 'all', deadline: '', anonymous: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create poll'); }
    finally { setCreatingPoll(false); }
  };

  const handleVote = async (pollId, optionId) => {
    try { await governanceAPI.castVote(pollId, { option_id: optionId }); toast.success('Vote cast'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to vote'); }
  };

  const handleClosePoll = async (pollId) => {
    if (!window.confirm('Close this poll? No more votes will be accepted.')) return;
    try { await governanceAPI.closePoll(pollId); toast.success('Poll closed'); fetchData(); }
    catch { toast.error('Failed to close poll'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input placeholder="Search polls..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-polls-hub" />
        </div>
        <Button onClick={() => setPollDialog(true)} className="bg-[#00A3A3] hover:bg-[#008585] shrink-0" data-testid="create-poll-btn">
          <Plus className="h-4 w-4 mr-2" />Create Poll
        </Button>
      </div>
      {polls.length === 0 ? (
        <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
          <Users className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
          <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No polls yet</h3>
          <p className="text-[#5A6570]">Create a poll to gather votes from your team.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {polls.filter(poll => (poll.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || (poll.description || '').toLowerCase().includes(searchTerm.toLowerCase())).map(poll => {
            const totalVotes = poll.options?.reduce((s, o) => s + (o.votes?.length || 0), 0) || 0;
            const hasVoted = poll.options?.some(o => o.votes?.some(v => v.user_id === user?.id));
            const isClosed = poll.status === 'closed';
            const isCreator = poll.created_by === user?.id;
            return (
              <Card key={poll.id} className={`border-[#E2E6EA] ${isClosed ? 'opacity-80' : ''}`} data-testid={`poll-${poll.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-[#1A1D21]">{poll.title}</h4>
                        <Badge variant="outline" className={`text-[10px] ${isClosed ? 'border-red-300 text-red-600' : 'border-green-300 text-green-600'}`}>{isClosed ? 'Closed' : 'Active'}</Badge>
                        {poll.deadline && <DeadlineBadge deadline={poll.deadline} compact />}
                      </div>
                      {poll.description && <p className="text-xs text-[#5A6570] mt-0.5">{poll.description}</p>}
                      <p className="text-[10px] text-[#A8B1B9] mt-1">By {poll.created_by_name} | {totalVotes} vote(s)</p>
                    </div>
                    {isCreator && !isClosed && (
                      <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleClosePoll(poll.id)}>Close Poll</Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {poll.options?.map(opt => {
                      const voteCount = opt.votes?.length || 0;
                      const pct = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
                      const myVote = opt.votes?.some(v => v.user_id === user?.id);
                      return (
                        <div key={opt.id} className="relative">
                          <button onClick={() => !hasVoted && !isClosed && handleVote(poll.id, opt.id)} disabled={hasVoted || isClosed}
                            className={`w-full text-left p-2.5 rounded-lg border transition-colors relative overflow-hidden ${myVote ? 'border-[#00A3A3] bg-[#00A3A3]/5' : 'border-[#E2E6EA] hover:border-[#00A3A3]/50'} ${hasVoted || isClosed ? 'cursor-default' : 'cursor-pointer'}`}
                            data-testid={`vote-option-${opt.id}`}>
                            {(hasVoted || isClosed) && <div className="absolute inset-0 bg-[#00A3A3]/5 rounded-lg" style={{ width: `${pct}%` }} />}
                            <div className="relative flex items-center justify-between">
                              <span className={`text-sm ${myVote ? 'font-semibold text-[#00A3A3]' : 'text-[#1A1D21]'}`}>
                                {myVote && <CheckCircle className="h-3.5 w-3.5 inline mr-1.5" />}{opt.text}
                              </span>
                              {(hasVoted || isClosed) && <span className="text-xs font-medium text-[#5A6570]">{pct}% ({voteCount})</span>}
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Poll Dialog */}
      <Dialog open={pollDialog} onOpenChange={setPollDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Poll</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Question *</Label><Input value={pollForm.title} onChange={(e) => setPollForm(p => ({...p, title: e.target.value}))} placeholder="What should we vote on?" className="border-[#E2E6EA]" data-testid="poll-title-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={pollForm.description} onChange={(e) => setPollForm(p => ({...p, description: e.target.value}))} placeholder="Optional context..." rows={2} className="border-[#E2E6EA]" /></div>
            <div className="space-y-1.5">
              <Label>Options *</Label>
              {pollForm.options.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={opt} onChange={(e) => { const o = [...pollForm.options]; o[i] = e.target.value; setPollForm(p => ({...p, options: o})); }} placeholder={`Option ${i + 1}`} className="border-[#E2E6EA]" data-testid={`poll-option-${i}`} />
                  {i >= 2 && <Button variant="ghost" size="sm" className="text-red-400 shrink-0" onClick={() => setPollForm(p => ({...p, options: p.options.filter((_,j) => j !== i)}))}>&times;</Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setPollForm(p => ({...p, options: [...p.options, '']}))}>
                <Plus className="h-3 w-3 mr-1" />Add Option
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Voter Group</Label>
                <Select value={pollForm.voter_group} onValueChange={(v) => setPollForm(p => ({...p, voter_group: v}))}>
                  <SelectTrigger className="border-[#E2E6EA]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem><SelectItem value="shareholders">Shareholders</SelectItem>
                    <SelectItem value="secretariat">Secretariat</SelectItem><SelectItem value="other_users">Other Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Deadline</Label><Input type="date" value={pollForm.deadline} onChange={(e) => setPollForm(p => ({...p, deadline: e.target.value}))} className="border-[#E2E6EA]" data-testid="poll-deadline-input" /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={pollForm.anonymous} onCheckedChange={(v) => setPollForm(p => ({...p, anonymous: v}))} /><Label className="text-sm">Anonymous voting</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPollDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePoll} disabled={creatingPoll} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="confirm-create-poll-btn">
              {creatingPoll && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Create Poll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PollsTab;
